import { useRouter } from 'next/router'
import React, { useEffect, useMemo } from 'react'
import {
  classifyAttribution,
  IAttribution
} from 'src/utils/marketing/classifyAttribution'
import { resolveLandingSurface } from 'src/utils/marketing/landingSurface'
import posthog from 'src/utils/pixels/posthog'
import {
  getExternalReferrer,
  getUTMsForAnalytics,
  storeUTMs,
  UTM_PARAMS
} from 'src/utils/utms'

// Persist the first-touch landing surface across SPA route changes so we
// can attribute later conversions back to the page the user actually
// arrived on, not the page they were on when they converted. Re-keyed
// per browser session.
const FIRST_SURFACE_KEY = 'landing_surface_first'

/**
 * Build the IAttribution shape the classifier expects from the cookies
 * `storeUTMs()` just wrote. Falls back to the external `document.referrer`
 * (`getExternalReferrer` — never our own site) for visitors who landed BEFORE
 * the referrer cookie was written (first `<UTMs />` mount of a session).
 * Cookies still win when present so
 * first-touch attribution survives intra-site navigation.
 */
const buildAttributionFromCookies = (): IAttribution => {
  const utms = getUTMsForAnalytics()
  return {
    utmSource: utms.utm_source,
    utmMedium: utms.utm_medium,
    utmCampaign: utms.utm_campaign,
    utmTerm: utms.utm_term,
    utmContent: utms.utm_content,
    utmPage: utms.utm_page,
    utmReferrer: utms.referrer || getExternalReferrer(),
    gclid: utms.gclid,
    fbclid: utms.fbclid,
    msclkid: utms.msclkid
  }
}

/**
 * Attribution params come from the ADDRESS BAR, not from `router.query`.
 *
 * On its own merits this just matches every other attribution consumer — gtag,
 * the Meta pixel and PostHog all read `window.location`, and the Apollo
 * authLink fallback parses `req.url` / `window.location.search`.
 *
 * It is also the guard against a whole class of silent failure. If a routing
 * proxy is ever added, `router.query` is built from what the SERVER saw: after
 * a `NextResponse.rewrite` it loses `gclid` / `utm_*` / `hsa_*` while
 * `router.asPath` and `window.location` keep the full query. Verified in a
 * browser on 2026-08-27 — with `<UTMs />` on `router.query`, a
 * `?gclid=…&utm_source=google` landing wrote NO attribution cookies and kept
 * the previous session's stale ones. That proxy was reverted (it did not change
 * the CDN cache key — see docs/performance/large-page-data.md), so nothing is
 * rewritten today, but the trap is one deploy away.
 *
 * Do NOT revert this to `router.query`.
 */
const readQueryFromUrl = (
  asPath: string
): Record<string, string | string[] | undefined> => {
  const queryStart = asPath.indexOf('?')
  if (queryStart === -1) return {}
  return Object.fromEntries(
    new URLSearchParams(asPath.slice(queryStart + 1)).entries()
  )
}

const readFirstSurface = (): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(FIRST_SURFACE_KEY)
  } catch {
    return null
  }
}

const writeFirstSurface = (surface: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(FIRST_SURFACE_KEY, surface)
  } catch {
    // sessionStorage unavailable (Safari private mode, etc.)
  }
}

const UTMs: React.FC = () => {
  const router = useRouter()

  const urlQuery = useMemo(
    () => (router.isReady ? readQueryFromUrl(router.asPath) : {}),
    [router.isReady, router.asPath]
  )

  const utmQueryString = useMemo(() => {
    if (!router.isReady) return ''
    return UTM_PARAMS.map((param) => urlQuery[param] || '').join('|')
  }, [router.isReady, urlQuery])

  useEffect(() => {
    if (!router.isReady) return

    const currentPath = router.asPath.split('?')[0]
    storeUTMs(urlQuery, currentPath)

    // Push our richer UTMs into PostHog as super-properties so they
    // ride on every event (autocapture + custom) — separate keys from
    // PostHog's native `$utm_*`. Reads the just-written cookies via
    // `getUTMsForAnalytics()` so cookie precedence (first-touch for
    // direct/organic, last-touch for UTM-tagged) is honored. Safe to
    // call pre-init: posthog.register goes through the queue.
    const utms = getUTMsForAnalytics()
    posthog.register(utms)

    // Mirror `classifyAttribution.ts` server-side: derive a single
    // (source, medium) pair from cookies + click IDs + referrer. Without
    // this, PostHog cohorts filtering on `utm_source=facebook` (paid)
    // or `utm_medium=organic` are silently empty — the raw URL only
    // ever carries `fbclid` / a Referer header for those buckets. With
    // it, every PostHog event carries the same attribution the
    // marketing report uses.
    const classified = classifyAttribution(buildAttributionFromCookies())
    posthog.register({
      classified_source: classified.utmSource ?? '(direct)',
      classified_medium: classified.utmMedium ?? '(none)'
    })

    // Surface taxonomy — disambiguates pages that share a `$pathname` but
    // behave like distinct landings (paid `?city=` deeplinks, SEO city
    // routes, sub-pillars). `landing_surface` updates on every route
    // change; `landing_surface_first` is sticky to the first surface
    // seen this session, for attribution back to the entry page.
    const currentSurface = resolveLandingSurface(router.asPath)
    const firstSurface = readFirstSurface()
    if (!firstSurface) writeFirstSurface(currentSurface)
    posthog.register({
      landing_surface: currentSurface,
      landing_surface_first: firstSurface ?? currentSurface
    })
  }, [router.isReady, router.asPath, urlQuery, utmQueryString])

  return null
}

export default UTMs
